import type { Project, Task } from '@taim/shared';
import { ChevronDown, ChevronUp, CirclePlus } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, type ReactNode, useId, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { ColorDot, FormError } from './components';
import { useCreateProject, useProjects } from './hooks';
import { ClientSelect } from './ProjectForm';

export type ProjectPickerValue = { projectId: string; taskId: string | null };

export type ProjectPickerProps = {
  value: ProjectPickerValue | null;
  onChange: (value: ProjectPickerValue | null) => void;
  placeholder?: string;
  /** Show "Create new project" (only ever to managers and admins, who may create projects). */
  allowCreate?: boolean;
  disabled?: boolean;
  className?: string;
  /** Names to show for `value` until the project list has loaded (e.g. from a report row). */
  fallback?: ProjectLabel | null;
  /**
   * List the projects this user may use instead of the viewer's (admins and managers editing
   * someone else's entry).
   */
  forUserId?: string;
  /** Shown (plainly, not as a call to action) when there is no project, e.g. "No project". */
  noneLabel?: string;
  /** Offer "No project" to clear a chosen project (off when the workspace requires one). */
  allowNone?: boolean;
  /** Highlights the field as missing (a required project). */
  invalid?: boolean;
};

export type ProjectLabel = {
  name: string;
  color: string;
  clientName: string | null;
  taskName: string | null;
};

type Row = { project: Project; tasks: Task[]; expanded: boolean; openTaskCount: number };
type Group = { key: string; label: string; rows: Row[] };

const includes = (text: string | null, q: string) => (text ?? '').toLowerCase().includes(q);

/** Filters active projects by search and groups them by client, NO CLIENT first (API order). */
function buildGroups(projects: Project[], search: string, expanded: ReadonlySet<string>): Group[] {
  const q = search.trim().toLowerCase();
  const groups: Group[] = [];
  for (const project of projects) {
    if (project.archived) continue;
    const open = project.tasks.filter((t) => !t.done);
    const projectMatches = !q || includes(project.name, q) || includes(project.clientName, q);
    const taskMatches = q ? open.filter((t) => includes(t.name, q)) : [];
    if (!projectMatches && taskMatches.length === 0) continue;
    // A search that only matches tasks shows those tasks straight away.
    const row: Row = projectMatches
      ? { project, tasks: open, expanded: expanded.has(project.id), openTaskCount: open.length }
      : { project, tasks: taskMatches, expanded: true, openTaskCount: open.length };
    const key = project.clientId ?? '';
    let group = groups.at(-1);
    if (!group || group.key !== key) {
      group = { key, label: (project.clientName ?? 'No client').toUpperCase(), rows: [] };
      groups.push(group);
    }
    group.rows.push(row);
  }
  return groups;
}

/** Moves focus between the options of the list with the arrow keys. */
function moveFocus(container: HTMLElement | null, from: EventTarget, step: 1 | -1) {
  const options = Array.from(container?.querySelectorAll<HTMLElement>('[role="option"]') ?? []);
  const index = options.indexOf(from as HTMLElement);
  const next = options[index === -1 ? (step === 1 ? 0 : options.length - 1) : index + step];
  next?.focus();
}

/**
 * Clockify's tracker project dropdown: search, projects grouped by client, expandable tasks,
 * and "Create new project" at the bottom.
 */
export function ProjectPicker({
  value,
  onChange,
  placeholder = 'Project',
  allowCreate = true,
  disabled,
  className,
  fallback,
  forUserId,
  noneLabel,
  allowNone = true,
  invalid,
}: ProjectPickerProps) {
  const { can } = useAuth();
  const canCreate = allowCreate && can('catalog:manage');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  // Archived projects are loaded too, so an old selection still shows its name. The viewer's own
  // list is shared and loaded up front; someone else's only when their picker opens (until then
  // the `fallback` label shows).
  const projects = useProjects({ archived: 'all', userId: forUserId }, !forUserId || open);
  const all = projects.data ?? [];

  const selected = value ? all.find((p) => p.id === value.projectId) : undefined;
  const selectedTask = value?.taskId ? selected?.tasks.find((t) => t.id === value.taskId) : undefined;
  const groups = useMemo(() => buildGroups(all, search, expanded), [all, search, expanded]);

  const setOpenState = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setSearch('');
      setCreating(false);
    }
  };
  const choose = (next: ProjectPickerValue | null) => {
    onChange(next);
    setOpenState(false);
  };
  const toggle = (projectId: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });

  const onSearchKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const first = groups[0]?.rows[0];
      if (!first) return;
      const matchedByTaskOnly = first.expanded && !expanded.has(first.project.id) && search.trim() !== '';
      choose({
        projectId: first.project.id,
        taskId: matchedByTaskOnly ? (first.tasks[0]?.id ?? null) : null,
      });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(listRef.current, e.target, 1);
    }
  };
  const onListKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(listRef.current, e.target, e.key === 'ArrowDown' ? 1 : -1);
    }
  };

  const shown: ProjectLabel | null = selected
    ? {
        name: selected.name,
        color: selected.color,
        clientName: selected.clientName,
        taskName: selectedTask?.name ?? null,
      }
    : value && !projects.data
      ? (fallback ?? null)
      : null;
  const label = shown ? `${shown.name}${shown.taskName ? `: ${shown.taskName}` : ''}` : null;

  return (
    <Popover open={open} onOpenChange={setOpenState}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          disabled={disabled}
          aria-busy={projects.isLoading}
          aria-invalid={invalid || undefined}
          aria-label={
            label
              ? `${placeholder}: ${label}${shown?.clientName ? ` - ${shown.clientName}` : ''}`
              : noneLabel
                ? `${placeholder}: ${noneLabel}`
                : placeholder
          }
          // Truncated names show in full on hover.
          title={label ? `${label}${shown?.clientName ? ` - ${shown.clientName}` : ''}` : undefined}
          className={cn(
            'max-w-full justify-start gap-1.5 px-2 font-normal',
            invalid && 'border border-destructive',
            className,
          )}
        >
          {shown && label ? (
            <>
              <ColorDot color={shown.color} />
              <span className="min-w-0 truncate" style={{ color: shown.color }}>
                {shown.name}
              </span>
              {shown.taskName && (
                // Its own span, so a long project name can't hide the task (rows of one project).
                <span className="min-w-[3rem] max-w-[50%] shrink-0 truncate" style={{ color: shown.color }}>
                  : {shown.taskName}
                </span>
              )}
              {shown.clientName && (
                // The client gives way first, so the project name stays readable.
                <span className="min-w-0 flex-1 truncate text-muted-foreground">- {shown.clientName}</span>
              )}
            </>
          ) : noneLabel ? (
            <span className="truncate text-muted-foreground">{noneLabel}</span>
          ) : (
            <span className={cn('flex items-center gap-1.5', invalid ? 'text-destructive' : 'text-primary')}>
              <CirclePlus className="size-4" />
              {placeholder}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Select project" align="start" className="w-[22rem] p-0">
        {creating ? (
          <QuickCreate
            projects={all}
            initialName={search.trim()}
            onCancel={() => setCreating(false)}
            onCreated={(p) => choose({ projectId: p.id, taskId: null })}
          />
        ) : (
          <>
            <div className="border-b p-2">
              <Input
                placeholder="Find project or client..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onSearchKey}
                autoFocus
              />
            </div>
            <div
              ref={listRef}
              role="listbox"
              aria-label="Projects"
              tabIndex={-1}
              onKeyDown={onListKey}
              className="max-h-80 min-h-0 overflow-y-auto py-1"
            >
              {value && allowNone && (
                <Option selected={false} onSelect={() => choose(null)} name="No project">
                  <span className="text-muted-foreground">No project</span>
                </Option>
              )}
              {projects.isPending && <p className="px-3 py-2 text-muted-foreground text-sm">Loading…</p>}
              {projects.isSuccess && groups.length === 0 && (
                <p className="px-3 py-2 text-muted-foreground text-sm">No matching projects</p>
              )}
              {groups.map((g) => (
                // biome-ignore lint/a11y/useSemanticElements: option group inside the custom listbox
                <div key={g.key} role="group" aria-label={g.label}>
                  <div className="px-3 pt-2 pb-1 text-[11px] text-muted-foreground tracking-wider">
                    {g.label}
                  </div>
                  {g.rows.map((row) => (
                    <ProjectRow
                      key={row.project.id}
                      row={row}
                      value={value}
                      onToggle={() => toggle(row.project.id)}
                      onSelect={choose}
                    />
                  ))}
                </div>
              ))}
            </div>
            {canCreate && (
              <div className="border-t p-1">
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full justify-start text-primary"
                  onClick={() => setCreating(true)}
                >
                  <CirclePlus /> Create new project
                </Button>
              </div>
            )}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

function Option({
  selected,
  onSelect,
  name,
  className,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  name: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div
      role="option"
      aria-selected={selected}
      data-name={name}
      tabIndex={-1}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-sm px-3 py-1.5 text-sm outline-none hover:bg-accent focus:bg-accent aria-selected:bg-accent/60',
        className,
      )}
    >
      {children}
    </div>
  );
}

function ProjectRow({
  row,
  value,
  onToggle,
  onSelect,
}: {
  row: Row;
  value: ProjectPickerValue | null;
  onToggle: () => void;
  onSelect: (v: ProjectPickerValue) => void;
}) {
  const { project, tasks, expanded, openTaskCount } = row;
  return (
    <div>
      <div className="flex items-center pr-2">
        <Option
          name={project.name}
          selected={value?.projectId === project.id}
          onSelect={() => onSelect({ projectId: project.id, taskId: null })}
        >
          <ColorDot color={project.color} />
          <span className="truncate" style={{ color: project.color }}>
            {project.name}
          </span>
        </Option>
        {openTaskCount > 0 && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-label={`Show tasks of ${project.name}`}
            onClick={onToggle}
            className="flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-muted-foreground text-xs hover:bg-muted"
          >
            {openTaskCount} {openTaskCount === 1 ? 'Task' : 'Tasks'}
            {expanded ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
          </button>
        )}
      </div>
      {expanded && tasks.length > 0 && (
        // biome-ignore lint/a11y/useSemanticElements: task options grouped under their project
        <div role="group" aria-label={`Tasks of ${project.name}`} className="pl-7">
          {tasks.map((t) => (
            <Option
              key={t.id}
              name={t.name}
              selected={value?.taskId === t.id}
              onSelect={() => onSelect({ projectId: project.id, taskId: t.id })}
            >
              {t.name}
            </Option>
          ))}
        </div>
      )}
    </div>
  );
}

/** The inline "Create new project" form at the bottom of the picker. */
function QuickCreate({
  projects,
  initialName,
  onCancel,
  onCreated,
}: {
  /** Known projects: an active one with the same name and client is selected instead of re-created. */
  projects: Project[];
  initialName: string;
  onCancel: () => void;
  onCreated: (p: Project) => void;
}) {
  const ids = { name: useId(), client: useId() };
  const [name, setName] = useState(initialName);
  const [clientId, setClientId] = useState('');
  const create = useCreateProject();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const key = name.trim().toLowerCase();
    const existing = projects.find(
      (p) => !p.archived && p.clientId === (clientId || null) && p.name.toLowerCase() === key,
    );
    if (existing) return onCreated(existing);
    create.mutate({ name, clientId: clientId || null }, { onSuccess: onCreated });
  };

  return (
    <form onSubmit={submit} className="grid gap-3 p-3">
      <div className="font-medium text-sm">Create new project</div>
      <div className="grid gap-1.5">
        <Label htmlFor={ids.name}>Project name</Label>
        <Input id={ids.name} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor={ids.client}>Client</Label>
        <ClientSelect id={ids.client} value={clientId} onChange={setClientId} />
      </div>
      <FormError error={create.error} />
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={create.isPending || !name.trim()}>
          CREATE
        </Button>
      </div>
    </form>
  );
}
